import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { ToastCtx } from "./toastContext";
import { cx } from "./cx";

export interface ToastOptions {
  tone?: "success" | "danger";
  action?: { label: string; onClick: () => void };
  /** 毫秒，默认 6000。 */
  duration?: number;
}

interface ToastItem extends ToastOptions { id: number; message: ReactNode }

/** 放在应用根部，之后用 useToast() 发送。 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const remove = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback((message: ReactNode, opts: ToastOptions = {}) => {
    const id = ++seq.current;
    setItems((xs) => [...xs, { id, message, ...opts }]);
    window.setTimeout(() => remove(id), opts.duration ?? 6000);
  }, [remove]);
  const value = useMemo(() => push, [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      {items.length > 0 && (
        <div className="ax-toast-stack">
          {items.map((t) => (
            <div key={t.id} className={cx("ax-toast", t.tone && `ax-toast-${t.tone}`)} role="status">
              {t.message}
              {t.action && (
                <button type="button" className="ax-btn ax-btn-ghost" onClick={() => { t.action?.onClick(); remove(t.id); }}>{t.action.label}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </ToastCtx.Provider>
  );
}
